// Rigorous §410(b) minimum-coverage math, modeled on 26 CFR 1.410(b)-2, -4, -5.
//
// A plan satisfies §410(b) if it passes EITHER:
//   - the ratio-percentage test: NHCE benefiting % >= 70% of HCE benefiting %, OR
//   - the average benefit test, which has TWO prongs that must BOTH hold:
//       (a) the nondiscriminatory-classification test (1.410(b)-4), and
//       (b) the average-benefit-percentage test (ABPT, 1.410(b)-5):
//           NHCE average benefit % >= 70% of HCE average benefit %.
//
// Classification test (1.410(b)-4): the plan's ratio percentage is compared to a
// "safe harbor" and "unsafe harbor" percentage that decline with the employer's
// NHCE concentration percentage:
//   safe harbor %   = 50% - 0.75 * (whole points NHCEconc exceeds 60%), floor 20%
//   unsafe harbor % = 40% - 0.75 * (whole points NHCEconc exceeds 60%), floor 20%
//   NHCE concentration % = nonexcludable NHCEs / all nonexcludable employees
// ratio >= safe -> classification passes; ratio < unsafe -> fails; in between is a
// facts-and-circumstances determination this screen cannot make.
//
// MODELED, not a formal test: this screen models the employer-benefit population
// the app determines (using the plan's age/service eligibility and treating LTPT
// as statutorily excludable). It cannot see the reasonableness of the plan's
// classification, contributions under other employer plans, related-employer
// aggregation, or the plan's actual testing elections. It is not a substitute for
// the plan administrator's formal coverage test.

export const COVERAGE_CITATION = '26 CFR 1.410(b)-2, -4, -5'

// Classification safe/unsafe harbor percentages as a function of the NHCE
// concentration percentage. The reg reduces by 3/4 point per WHOLE point above 60.
export function classificationSafeHarborPercent(concentrationPercent) {
  const excess = Math.max(0, Math.floor(Number(concentrationPercent) || 0) - 60)
  return Math.max(20, 50 - 0.75 * excess)
}

export function classificationUnsafePercent(concentrationPercent) {
  const excess = Math.max(0, Math.floor(Number(concentrationPercent) || 0) - 60)
  return Math.max(20, 40 - 0.75 * excess)
}

// NHCE concentration percentage: share of the nonexcludable workforce that is NHCE.
export function nhceConcentrationPercent(nonexcludableNhceCount, nonexcludableTotalCount) {
  const total = Number(nonexcludableTotalCount) || 0
  if (total <= 0) return 0
  return (Math.max(0, Number(nonexcludableNhceCount) || 0) / total) * 100
}

// The ratio-percentage figure: NHCE benefiting rate over HCE benefiting rate.
export function ratioPercent(nhceBenefitingRate, hceBenefitingRate) {
  const hce = Number(hceBenefitingRate) || 0
  const nhce = Number(nhceBenefitingRate) || 0
  if (hce > 0) return (nhce / hce) * 100
  return nhce > 0 ? 100 : 0
}

// Full §410(b) evaluation. Inputs are already-computed group counts and rates so
// the module stays pure and independently testable.
export function evaluateCoverage({
  hceBenefiting = 0,
  nonexcludableHceCount = 0,
  nhceBenefiting = 0,
  nonexcludableNhceCount = 0,
  // Average benefit percentages (contributions basis) for the ABPT prong.
  hceAvgBenefitPercent = null,
  nhceAvgBenefitPercent = null,
} = {}) {
  const populationOk = nonexcludableHceCount > 0 && nonexcludableNhceCount > 0
  const hasBenefit = (hceBenefiting + nhceBenefiting) > 0
  const available = populationOk && hasBenefit
  const unavailableReason = populationOk
    ? (hasBenefit ? null : 'no-employer-contributions')
    : 'insufficient-population'

  const hceRate = nonexcludableHceCount > 0 ? (hceBenefiting / nonexcludableHceCount) * 100 : 0
  const nhceRate = nonexcludableNhceCount > 0 ? (nhceBenefiting / nonexcludableNhceCount) * 100 : 0
  const ratio = available ? ratioPercent(nhceRate, hceRate) : null
  const ratioStatus = !available
    ? 'unavailable'
    : ratio >= 70 ? 'passes-ratio' : 'under-ratio'

  // Nondiscriminatory classification prong.
  const nonexcludableTotal = nonexcludableHceCount + nonexcludableNhceCount
  const concentrationPercent = nhceConcentrationPercent(nonexcludableNhceCount, nonexcludableTotal)
  const safeHarborPercent = classificationSafeHarborPercent(concentrationPercent)
  const unsafePercent = classificationUnsafePercent(concentrationPercent)
  const classificationStatus = !available
    ? 'unavailable'
    : ratio >= safeHarborPercent
      ? 'safe'
      : ratio >= unsafePercent
        ? 'facts-and-circumstances'
        : 'unsafe'

  // Average-benefit-percentage prong (contributions basis).
  const abptAvailable = available
    && Number.isFinite(Number(hceAvgBenefitPercent))
    && Number.isFinite(Number(nhceAvgBenefitPercent))
  const abptRatio = abptAvailable
    ? (Number(hceAvgBenefitPercent) > 0
        ? (Number(nhceAvgBenefitPercent) / Number(hceAvgBenefitPercent)) * 100
        : Number(nhceAvgBenefitPercent) > 0 ? 100 : 0)
    : null
  const abptStatus = !abptAvailable ? 'unavailable' : abptRatio >= 70 ? 'pass' : 'fail'

  // Average benefit test overall = classification (not unsafe) AND ABPT pass. A
  // facts-and-circumstances classification is surfaced as "uncertain", never a pass.
  let averageBenefitTestStatus
  if (!available || abptStatus === 'unavailable') {
    averageBenefitTestStatus = 'unavailable'
  } else if (classificationStatus === 'unsafe' || abptStatus === 'fail') {
    averageBenefitTestStatus = 'fail'
  } else if (classificationStatus === 'facts-and-circumstances') {
    averageBenefitTestStatus = 'uncertain'
  } else {
    averageBenefitTestStatus = 'pass'
  }

  // Overall §410(b): pass on the ratio test, else lean on the average benefit test.
  let status
  if (!available) {
    status = 'unavailable'
  } else if (ratioStatus === 'passes-ratio') {
    status = 'passes-ratio'
  } else if (averageBenefitTestStatus === 'pass') {
    status = 'passes-average-benefit'
  } else if (averageBenefitTestStatus === 'uncertain') {
    status = 'may-pass-average-benefit'
  } else {
    status = 'fails'
  }

  return {
    available,
    unavailableReason,
    hceBenefitingRate: hceRate,
    nhceBenefitingRate: nhceRate,
    ratioPercent: ratio,
    ratioStatus,
    concentrationPercent,
    safeHarborPercent,
    unsafePercent,
    classificationStatus,
    abptRatio,
    abptStatus,
    averageBenefitTestStatus,
    status,
  }
}
